import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArtifactLogo } from "@/components/ui/artifact-logo";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { signUpWithMagicLink } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store";
import { prepareForOAuthLogin } from "@/utils/auth";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Info,
  Mail,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

export function SignInCard({ isVisible, onClose, className, ...props }) {
  const { loginWithGitHub, loginWithGoogle, loginWithLinkedIn, isLoading } =
    useAuthStore();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailVerificationIssue, setEmailVerificationIssue] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleMagicLinkSignup = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!isValidEmail(email)) {
      setError(
        "Please enter a valid email address with a proper domain (e.g., .com)"
      );
      return;
    }

    if (!displayName) {
      setError("Please enter your display name");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data, error } = await signUpWithMagicLink(email, displayName);

      if (error) {
        console.error("Magic link signup error:", error);

        if (
          error.message?.includes("send email") ||
          error.message?.includes("gomail") ||
          error.message?.includes("domain") ||
          error.message?.includes("confirmation") ||
          error.code === "email_verification_issue"
        ) {
          setEmailVerificationIssue(true);
          setError(
            "Unable to send verification email. Please use social login instead."
          );
        } else {
          setError(error.message || "Failed to send magic link");
        }
        return;
      }

      setMagicLinkSent(true);
      setError(null);
    } catch (error) {
      console.error("Error in magic link signup process:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGitHubSignup = async () => {
    try {
      localStorage.removeItem("email_verification_issue");
      await prepareForOAuthLogin();

      const result = await loginWithGitHub();

      if (!result.success) {
        throw new Error(
          result.error?.message || "Failed to connect with GitHub"
        );
      }
    } catch (error) {
      console.error("GitHub signup error:", error);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      localStorage.removeItem("email_verification_issue");
      await prepareForOAuthLogin();

      const result = await loginWithGoogle();

      if (!result.success) {
        throw new Error(
          result.error?.message || "Failed to connect with Google"
        );
      }
    } catch (error) {
      console.error("Google signup error:", error);
    }
  };

  const handleLinkedInSignup = async () => {
    try {
      localStorage.removeItem("email_verification_issue");
      await prepareForOAuthLogin();

      const result = await loginWithLinkedIn();

      if (!result.success) {
        throw new Error(
          result.error?.message || "Failed to connect with LinkedIn"
        );
      }
    } catch (error) {
      console.error("LinkedIn signup error:", error);
    }
  };

  const resetForm = () => {
    setMagicLinkSent(false);
    setError(null);
    setEmail("");
    setDisplayName("");
  };

  if (!isVisible) return null;

  // Magic Link Sent Success State
  if (magicLinkSent) {
    return (
      <Card
        className={cn(
          "fixed bottom-4 right-4 w-96 shadow-2xl border-0 backdrop-blur-sm z-[9999] bg-white dark:bg-[#1E1E1E]",
          className
        )}
        {...props}
      >
        <CardHeader className="relative pb-4">
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-2 top-2 h-5 w-5 p-0 bg-transparent hover:bg-transparent border-none"
            onClick={onClose}
          >
            <X className="h-3 w-3 text-gray-600 dark:text-white hover:text-gray-800 dark:hover:text-gray-300 transition-colors" />
          </Button>
          <div className="flex flex-col items-center text-center space-y-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <Mail className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">
                Check your email
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                We&apos;ve sent a magic link to{" "}
                <span className="font-medium text-foreground">{email}</span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          <Alert className="bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
            <CheckCircle className="h-3 w-3" />
            <AlertDescription className="space-y-1">
              <p className="text-xs">
                Click the link in your email to complete your account setup.
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">
                The link will expire in 1 hour. Check your spam folder if you
                don&apos;t see it.
              </p>
            </AlertDescription>
          </Alert>

          <Button
            type="button"
            variant="outline"
            className="w-full h-8 text-sm text-gray-700 dark:text-white !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
            onClick={resetForm}
          >
            <ArrowLeft className="h-3 w-3 mr-1" />
            Try a different email
          </Button>

          <TooltipProvider>
            <div className="grid gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block w-full">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-8 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 relative text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                      onClick={handleGoogleSignup}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4 mr-1">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                        <path d="M1 1h22v22H1z" fill="none" />
                      </svg>
                      <span className="ml-1 text-muted-foreground">
                        Continue with Google
                      </span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Login with Google</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block w-full">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-8 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 relative text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                      onClick={handleGitHubSignup}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="h-4 w-4 mr-1"
                      >
                        <path
                          d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
                          fill="currentColor"
                        />
                      </svg>
                      <span className="ml-1 text-muted-foreground">
                        Continue with GitHub
                      </span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Login with GitHub</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block w-full">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-8 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 relative text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                      onClick={handleLinkedInSignup}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="h-4 w-4 mr-1"
                        fill="#0077B5"
                      >
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      <span className="ml-1 text-muted-foreground">
                        Continue with LinkedIn
                      </span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Login with LinkedIn</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          <div className="text-center text-sm">
            Already have an account?{" "}
            <Link
              to="/auth/login"
              className="font-medium text-primary hover:underline"
            >
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Main signup form
  return (
    <Card
      className={cn(
        "fixed bottom-4 right-4 w-72 shadow-2xl border-0 backdrop-blur-sm z-[9999] bg-white dark:bg-[#1E1E1E]",
        className
      )}
      {...props}
      style={{
        zIndex: 9999,
      }}
    >
      <CardHeader className="relative pb-4">
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-2 top-2 h-5 w-5 p-0 bg-transparent hover:bg-transparent border-none"
          onClick={onClose}
        >
          <X className="h-3 w-3 text-gray-600 dark:text-white" />
        </Button>
        <div className="flex flex-col items-center text-center space-y-2">
          <ArtifactLogo className="h-8 w-8" />
          <div>
            <CardTitle className="text-lg font-semibold">
              Sign in or create an account
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Unlock Pro Search and History
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {emailVerificationIssue && (
          <Alert
            variant="warning"
            className="bg-amber-50 text-amber-800 border-amber-200"
          >
            <Info className="h-4 w-4" />
            <AlertDescription className="flex flex-col gap-2">
              <p>
                We&apos;re experiencing issues with email verification. For
                fastest signup, please use Google or GitHub login instead.
              </p>
              <TooltipProvider>
                <div className="grid gap-2 mt-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block w-full">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-gray-700 dark:text-white relative hover:bg-gray-100 dark:hover:bg-zinc-700 !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                          onClick={handleGoogleSignup}
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4 mr-1">
                            <path
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              fill="#4285F4"
                            />
                            <path
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              fill="#34A853"
                            />
                            <path
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              fill="#FBBC05"
                            />
                            <path
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              fill="#EA4335"
                            />
                            <path d="M1 1h22v22H1z" fill="none" />
                          </svg>
                          Continue with Google
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Login with Google</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block w-full">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-gray-700 dark:text-white relative hover:bg-gray-100 dark:hover:bg-zinc-700 !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                          onClick={handleGitHubSignup}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="h-4 w-4 mr-1"
                          >
                            <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                          </svg>
                          Continue with GitHub
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Login with GitHub</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block w-full">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full text-gray-700 dark:text-white relative hover:bg-gray-100 dark:hover:bg-zinc-700 !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                          onClick={handleLinkedInSignup}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="h-5 w-5 mr-2"
                            fill="#0077B5"
                          >
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                          Continue with LinkedIn
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Login with LinkedIn</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </AlertDescription>
          </Alert>
        )}

        {/* OAuth Buttons */}
        <TooltipProvider>
          <div className="grid gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block w-full">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-9 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 relative text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                    onClick={handleGoogleSignup}
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                      <path d="M1 1h22v22H1z" fill="none" />
                    </svg>
                    <span className="ml-2 text-muted-foreground">
                      Continue with Google
                    </span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Login with Google</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block w-full">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-9 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 relative text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                    onClick={handleGitHubSignup}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="h-4 w-4 mr-2"
                      fill="currentColor"
                    >
                      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                    </svg>
                    <span className="ml-2 text-muted-foreground">
                      Continue with GitHub
                    </span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Login with GitHub</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block w-full">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-9 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-700 relative text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                    onClick={handleLinkedInSignup}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="h-4 w-4 mr-2"
                      fill="#0077B5"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    <span className="ml-2 text-muted-foreground">
                      Continue with LinkedIn
                    </span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Login with LinkedIn</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>

        <div className="relative flex items-center text-xs">
          <div className="flex-grow border-t border-gray-300 dark:border-zinc-600"></div>
          <span className="px-2 bg-white dark:bg-zinc-900 text-gray-500 dark:text-zinc-400">
            Or continue with email
          </span>
          <div className="flex-grow border-t border-gray-300 dark:border-zinc-600"></div>
        </div>

        {/* Email Input */}
        <form onSubmit={handleMagicLinkSignup} className="space-y-3">
          <div className="space-y-1">
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || isSubmitting}
              className="h-9 text-foreground placeholder-muted-foreground text-sm !bg-gray-100 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
            />
          </div>

          <AnimatePresence>
            {email && isValidEmail(email) && (
              <motion.div
                initial={{ opacity: 0, height: 0, y: -10 }}
                animate={{ opacity: 1, height: "auto", y: 0 }}
                exit={{ opacity: 0, height: 0, y: -10 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="grid gap-1 py-1"
              >
                <Label htmlFor="displayName" className="text-xs">
                  Display Name
                </Label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Your name"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isLoading || isSubmitting}
                  className="h-9 text-foreground placeholder-muted-foreground text-sm !bg-gray-100 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            type="submit"
            variant="outline"
            className="w-full h-9 hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-700 dark:text-white font-medium text-sm !bg-gray-50 dark:!bg-zinc-800 border-gray-300 dark:border-zinc-600"
            disabled={
              isLoading ||
              isSubmitting ||
              emailVerificationIssue ||
              !email ||
              !isValidEmail(email) ||
              (email && isValidEmail(email) && !displayName)
            }
          >
            {isLoading || isSubmitting
              ? "Sending Magic Link..."
              : "Continue with Email"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default SignInCard;
